import React, { useEffect, useState, useCallback } from "react";
import {
  Spin, Alert, Tag, Input, Button, Drawer, Form, message, Badge,
  Tooltip, Popconfirm, Tabs, Modal, Select, Empty,
} from "antd";
import {
  PlusOutlined, DeleteOutlined, FileTextOutlined, CloudUploadOutlined,
  ThunderboltOutlined, EditOutlined, EyeOutlined, BookOutlined,
  FileOutlined, CheckCircleOutlined, LoadingOutlined,
} from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import customClientAxios from "../../customAxios";
import { PageContainer } from "../paUtils";
import styled from "styled-components";

const API_BASE = "/seo-blogs";

// ── Styled Components ────────────────────────────────────────────────────────

const StatsBar = styled.div`
  display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap;
`;
const StatPill = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 8px 16px; border-radius: 10px;
  background: white; border: 1px solid #e2e8f0;
  font-size: 13px; color: #374151;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
`;
const StatNum = styled.span`font-weight: 700; color: #1e293b; font-size: 16px;`;
const StatLabel = styled.span`color: #64748b; font-size: 12px;`;

const KanbanContainer = styled.div`
  display: flex; gap: 16px; overflow-x: auto;
  padding-bottom: 16px; min-height: 500px;
`;
const KanbanColumn = styled.div`
  min-width: 280px; width: 280px; flex-shrink: 0;
  background: #f8fafc; border-radius: 14px;
  border: 1px solid #e2e8f0; display: flex;
  flex-direction: column; max-height: 75vh;
`;
const KanbanColumnHeader = styled.div`
  padding: 14px 16px; font-size: 14px; font-weight: 600;
  color: #1e293b; border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0; display: flex; flex-direction: column; gap: 6px;
  border-top: 3px solid ${(p) => p.$color || "#6366f1"};
  border-radius: 14px 14px 0 0;
`;
const ColumnTitle = styled.div`
  display: flex; align-items: center; justify-content: space-between;
`;
const ColumnDesc = styled.div`
  font-size: 11px; color: #94a3b8; font-weight: 400;
  line-height: 1.4;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
`;
const ColumnKeywords = styled.div`
  display: flex; gap: 4px; flex-wrap: wrap; margin-top: 2px;
`;
const KanbanCards = styled.div`
  flex: 1; overflow-y: auto; padding: 10px;
  display: flex; flex-direction: column; gap: 10px;
  scrollbar-width: thin;
`;
const KanbanCard = styled.div`
  background: white; border-radius: 10px; padding: 14px;
  border: 1px solid #e2e8f0; cursor: pointer; overflow: hidden;
  transition: all 0.15s ease; box-shadow: 0 1px 2px rgba(0,0,0,0.04);
  &:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.08); transform: translateY(-1px); }
`;
const CardTitle = styled.div`font-size: 13px; font-weight: 600; color: #1e293b; margin-bottom: 4px;`;
const CardDesc = styled.div`
  font-size: 11px; color: #94a3b8; margin-bottom: 8px;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
`;
const CardFooter = styled.div`display: flex; gap: 4px; flex-wrap: wrap; align-items: center;`;

const AddCardBtn = styled.button`
  width: 100%; padding: 10px; border: 1px dashed #cbd5e1;
  border-radius: 8px; background: transparent; color: #94a3b8;
  font-size: 12px; cursor: pointer; transition: all 0.15s ease;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  &:hover { border-color: #6366f1; color: #6366f1; background: rgba(99,102,241,0.04); }
`;

const AddColumnBtn = styled.button`
  min-width: 280px; width: 280px; flex-shrink: 0; height: fit-content;
  padding: 40px 20px; border: 2px dashed #cbd5e1;
  border-radius: 14px; background: transparent; color: #94a3b8;
  font-size: 14px; cursor: pointer; transition: all 0.15s ease;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px;
  &:hover { border-color: #6366f1; color: #6366f1; background: rgba(99,102,241,0.04); }
`;

const KnowledgeCard = styled.div`
  background: white; border-radius: 12px; padding: 16px;
  border: 1px solid #e2e8f0; display: flex; align-items: center;
  gap: 14px; transition: all 0.15s ease;
  &:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.06); }
`;
const KnowledgeGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 12px;
`;

const BlogPreview = styled.div`
  padding: 24px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  max-height: 60vh;
  overflow-y: auto;
  line-height: 1.7;
  font-size: 15px;
  color: #334155;
  h2 { font-size: 22px; font-weight: 700; color: #1e293b; margin: 24px 0 12px; }
  h3 { font-size: 18px; font-weight: 600; color: #1e293b; margin: 20px 0 10px; }
  p { margin: 0 0 14px; }
  ul, ol { margin: 0 0 14px; padding-left: 24px; }
  li { margin-bottom: 6px; }
  strong { color: #1e293b; }
  a { color: #6366f1; }
`;

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  idea: { color: "default", label: "Idea" },
  generating: { color: "processing", label: "Generating" },
  review: { color: "warning", label: "Review" },
  published: { color: "success", label: "Published" },
};

const PILLAR_COLORS = [
  "#6366f1", "#10b981", "#f59e0b", "#ec4899", "#3b82f6",
  "#14b8a6", "#ef4444", "#8b5cf6", "#f97316", "#06b6d4",
];

// ── Component ─────────────────────────────────────────────────────────────────

function SEOBlogsPage() {
  const [activeTab, setActiveTab] = useState("kanban");
  const [pillars, setPillars] = useState([]);
  const [knowledgeDocs, setKnowledgeDocs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pillar drawer
  const [pillarDrawerOpen, setPillarDrawerOpen] = useState(false);
  const [editingPillar, setEditingPillar] = useState(null);
  const [pillarForm] = Form.useForm();
  const [pillarSubmitting, setPillarSubmitting] = useState(false);

  // Blog drawer
  const [blogDrawerOpen, setBlogDrawerOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [blogForm] = Form.useForm();
  const [blogSubmitting, setBlogSubmitting] = useState(false);
  const [targetPillarId, setTargetPillarId] = useState(null);

  // Blog preview/edit modal
  const [previewBlog, setPreviewBlog] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editMode, setEditMode] = useState("preview"); // "preview" | "visual" | "html"
  const [contentValue, setContentValue] = useState("");
  const [contentSaving, setContentSaving] = useState(false);

  // AI enhancement
  const [enhancePrompt, setEnhancePrompt] = useState("");
  const [enhancing, setEnhancing] = useState(false);

  // Generating state
  const [generatingBlogId, setGeneratingBlogId] = useState(null);

  // Knowledge text drawer
  const [textDrawerOpen, setTextDrawerOpen] = useState(false);
  const [textForm] = Form.useForm();
  const [textSubmitting, setTextSubmitting] = useState(false);

  // Test knowledge
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testQuery, setTestQuery] = useState("");
  const [testResults, setTestResults] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchPillars = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await customClientAxios().get(`${API_BASE}/pillars`);
      setPillars(res.data.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load pillars");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchKnowledge = useCallback(async () => {
    try {
      const res = await customClientAxios().get(`${API_BASE}/knowledge`);
      setKnowledgeDocs(res.data.data || []);
    } catch {}
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await customClientAxios().get(`${API_BASE}/stats`);
      setStats(res.data.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchPillars();
    fetchKnowledge();
    fetchStats();
  }, [fetchPillars, fetchKnowledge, fetchStats]);

  const refresh = () => { fetchPillars(); fetchKnowledge(); fetchStats(); };

  // ── Pillar CRUD ────────────────────────────────────────────────────────────

  const openCreatePillar = () => {
    setEditingPillar(null);
    pillarForm.resetFields();
    pillarForm.setFieldsValue({ color: PILLAR_COLORS[pillars.length % PILLAR_COLORS.length] });
    setPillarDrawerOpen(true);
  };

  const openEditPillar = (pillar) => {
    setEditingPillar(pillar);
    pillarForm.setFieldsValue({
      title: pillar.title,
      description: pillar.description,
      keywords: (pillar.keywords || []).join(", "),
      color: pillar.color,
    });
    setPillarDrawerOpen(true);
  };

  const handlePillarSubmit = async (values) => {
    setPillarSubmitting(true);
    try {
      const data = {
        ...values,
        keywords: values.keywords ? values.keywords.split(",").map((k) => k.trim()).filter(Boolean) : [],
      };
      if (editingPillar) {
        await customClientAxios().patch(`${API_BASE}/pillars/${editingPillar._id}`, data);
        message.success("Pillar updated");
      } else {
        await customClientAxios().post(`${API_BASE}/pillars`, data);
        message.success("Pillar created");
      }
      setPillarDrawerOpen(false);
      refresh();
    } catch (e) {
      message.error(e?.response?.data?.error || "Failed to save pillar");
    } finally {
      setPillarSubmitting(false);
    }
  };

  const handleDeletePillar = async (pillarId) => {
    try {
      await customClientAxios().delete(`${API_BASE}/pillars/${pillarId}`);
      message.success("Pillar deleted");
      refresh();
    } catch (e) {
      message.error(e?.response?.data?.error || "Failed to delete pillar");
    }
  };

  // ── Blog CRUD ──────────────────────────────────────────────────────────────

  const openCreateBlog = (pillarId) => {
    setEditingBlog(null);
    setTargetPillarId(pillarId);
    blogForm.resetFields();
    setBlogDrawerOpen(true);
  };

  const openEditBlog = (blog) => {
    setEditingBlog(blog);
    setTargetPillarId(blog.pillarId);
    blogForm.setFieldsValue({
      title: blog.title,
      description: blog.description,
      keywords: (blog.keywords || []).join(", "),
      publishedUrl: blog.publishedUrl || "",
    });
    setBlogDrawerOpen(true);
  };

  const handleBlogSubmit = async (values) => {
    setBlogSubmitting(true);
    try {
      const data = {
        ...values,
        keywords: values.keywords ? values.keywords.split(",").map((k) => k.trim()).filter(Boolean) : [],
      };
      if (editingBlog) {
        await customClientAxios().patch(`${API_BASE}/blogs/${editingBlog._id}`, data);
        message.success("Blog updated");
      } else {
        data.pillarId = targetPillarId;
        await customClientAxios().post(`${API_BASE}/blogs`, data);
        message.success("Blog card created");
      }
      setBlogDrawerOpen(false);
      refresh();
    } catch (e) {
      message.error(e?.response?.data?.error || "Failed to save blog");
    } finally {
      setBlogSubmitting(false);
    }
  };

  const handleDeleteBlog = async (blogId) => {
    try {
      await customClientAxios().delete(`${API_BASE}/blogs/${blogId}`);
      message.success("Blog deleted");
      refresh();
    } catch (e) {
      message.error(e?.response?.data?.error || "Failed to delete blog");
    }
  };

  const handleStatusChange = async (blogId, newStatus) => {
    try {
      await customClientAxios().patch(`${API_BASE}/blogs/${blogId}`, { status: newStatus });
      refresh();
    } catch (e) {
      message.error("Failed to update status");
    }
  };

  // ── Blog Generation ────────────────────────────────────────────────────────

  const handleGenerate = async (blogId) => {
    setGeneratingBlogId(blogId);
    try {
      const res = await customClientAxios().post(`${API_BASE}/blogs/${blogId}/generate`);
      message.success("Blog generated! Review the content.");
      setPreviewBlog(res.data.data);
      setPreviewOpen(true);
      refresh();
    } catch (e) {
      message.error(e?.response?.data?.error || "Generation failed");
    } finally {
      setGeneratingBlogId(null);
    }
  };

  // ── Blog Preview/Edit ─────────────────────────────────────────────────────

  const openPreview = (blog) => {
    setPreviewBlog(blog);
    setContentValue(blog.content || "");
    setEditMode("preview");
    setEnhancePrompt("");
    setPreviewOpen(true);
  };

  const handleSaveContent = async () => {
    if (!previewBlog) return;
    setContentSaving(true);
    try {
      const res = await customClientAxios().patch(`${API_BASE}/blogs/${previewBlog._id}`, { content: contentValue });
      message.success("Content saved");
      setPreviewBlog(res.data.data);
      setEditMode("preview");
      refresh();
    } catch (e) {
      message.error("Failed to save content");
    } finally {
      setContentSaving(false);
    }
  };

  const handleEnhance = async () => {
    if (!previewBlog || !enhancePrompt.trim()) return;
    setEnhancing(true);
    try {
      const res = await customClientAxios().post(`${API_BASE}/blogs/${previewBlog._id}/enhance`, {
        instructions: enhancePrompt,
      });
      const updated = res.data.data;
      setPreviewBlog(updated);
      setContentValue(updated.content || "");
      setEnhancePrompt("");
      message.success("Blog enhanced");
      refresh();
    } catch (e) {
      message.error(e?.response?.data?.error || "Enhancement failed");
    } finally {
      setEnhancing(false);
    }
  };

  // ── Knowledge Base ─────────────────────────────────────────────────────────

  const handleAddTextKnowledge = async (values) => {
    setTextSubmitting(true);
    try {
      await customClientAxios().post(`${API_BASE}/knowledge/text`, values);
      message.success("Knowledge added and indexed");
      setTextDrawerOpen(false);
      textForm.resetFields();
      refresh();
    } catch (e) {
      message.error(e?.response?.data?.error || "Failed to add knowledge");
    } finally {
      setTextSubmitting(false);
    }
  };

  const fileInputRef = React.useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelected = async (e) => {
    const rawFile = e.target.files?.[0];
    if (!rawFile) return;
    e.target.value = "";

    const fileName = rawFile.name;
    const contentType = rawFile.type || "application/octet-stream";
    const fileSize = rawFile.size || 0;

    setUploading(true);
    try {
      // 1. Get signed upload URL from backend
      const urlRes = await customClientAxios().post(`${API_BASE}/knowledge/upload-url`, {
        fileName,
        contentType,
        fileSize,
      });
      const { uploadUrl, publicUrl } = urlRes.data.data;

      // 2. Upload file directly to GCS
      await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType },
        body: rawFile,
      });

      // 3. Create the doc record with the CDN URL
      const docRes = await customClientAxios().post(`${API_BASE}/knowledge`, {
        fileName,
        fileUrl: publicUrl,
        fileType: contentType,
        fileSize,
        description: `Uploaded file: ${fileName}`,
      });
      const docId = docRes.data.data._id;

      // 4. Index — backend downloads from GCS and extracts text
      await customClientAxios().post(`${API_BASE}/knowledge/${docId}/index`);
      message.success("Document uploaded and indexed");
      refresh();
    } catch (err) {
      message.error(err?.response?.data?.error || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteKnowledge = async (docId) => {
    try {
      await customClientAxios().delete(`${API_BASE}/knowledge/${docId}`);
      message.success("Document deleted");
      refresh();
    } catch (e) {
      message.error("Failed to delete document");
    }
  };

  const handleTestKnowledge = async () => {
    if (!testQuery.trim()) return;
    setTestLoading(true);
    setTestResults(null);
    try {
      const res = await customClientAxios().post(`${API_BASE}/knowledge/test`, { query: testQuery });
      setTestResults(res.data.data);
    } catch (e) {
      message.error("Failed to search knowledge base");
    } finally {
      setTestLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <PageContainer>
      {/* Stats Bar */}
      {stats && (
        <StatsBar>
          <StatPill><StatNum>{stats.pillars}</StatNum><StatLabel>Pillars</StatLabel></StatPill>
          <StatPill><StatNum>{stats.totalBlogs}</StatNum><StatLabel>Total Blogs</StatLabel></StatPill>
          <StatPill>
            <Tag color="default" style={{ margin: 0, fontSize: 11 }}>Idea</Tag>
            <StatNum style={{ fontSize: 14 }}>{stats.byStatus?.idea || 0}</StatNum>
          </StatPill>
          <StatPill>
            <Tag color="warning" style={{ margin: 0, fontSize: 11 }}>Review</Tag>
            <StatNum style={{ fontSize: 14 }}>{stats.byStatus?.review || 0}</StatNum>
          </StatPill>
          <StatPill>
            <Tag color="success" style={{ margin: 0, fontSize: 11 }}>Published</Tag>
            <StatNum style={{ fontSize: 14 }}>{stats.byStatus?.published || 0}</StatNum>
          </StatPill>
          <StatPill><StatNum>{stats.knowledgeDocs}</StatNum><StatLabel>Knowledge Docs</StatLabel></StatPill>
        </StatsBar>
      )}

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        style={{ marginBottom: 16 }}
        items={[
          { key: "kanban", label: <span><BookOutlined style={{ marginRight: 6 }} />Pillars & Blogs</span> },
          { key: "knowledge", label: <span><FileTextOutlined style={{ marginRight: 6 }} />Knowledge Base</span> },
        ]}
      />

      {error && <Alert type="error" message={error} style={{ marginBottom: 20 }} />}

      {/* ── Kanban Tab ─────────────────────────────────────────────────────── */}
      {activeTab === "kanban" && (
        <Spin spinning={loading}>
          <KanbanContainer>
            {pillars.map((pillar) => (
              <KanbanColumn key={pillar._id}>
                <KanbanColumnHeader $color={pillar.color}>
                  <ColumnTitle>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span>{pillar.title}</span>
                      <Badge count={pillar.blogs?.length || 0} style={{ backgroundColor: "#94a3b8" }} size="small" />
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <Tooltip title="Edit pillar">
                        <Button size="small" type="text" icon={<EditOutlined />} style={{ color: "#94a3b8", fontSize: 12 }}
                          onClick={() => openEditPillar(pillar)} />
                      </Tooltip>
                      <Popconfirm title="Delete this pillar?" onConfirm={() => handleDeletePillar(pillar._id)}>
                        <Button size="small" type="text" icon={<DeleteOutlined />} style={{ color: "#94a3b8", fontSize: 12 }} />
                      </Popconfirm>
                    </div>
                  </ColumnTitle>
                  {pillar.description && <ColumnDesc>{pillar.description}</ColumnDesc>}
                  {pillar.keywords?.length > 0 && (
                    <ColumnKeywords>
                      {pillar.keywords.slice(0, 4).map((k) => (
                        <Tag key={k} style={{ fontSize: 9, margin: 0, padding: "0 4px", lineHeight: "18px" }}>{k}</Tag>
                      ))}
                      {pillar.keywords.length > 4 && (
                        <Tag style={{ fontSize: 9, margin: 0, padding: "0 4px", lineHeight: "18px" }}>
                          +{pillar.keywords.length - 4}
                        </Tag>
                      )}
                    </ColumnKeywords>
                  )}
                </KanbanColumnHeader>
                <KanbanCards>
                  {(pillar.blogs || []).map((blog) => (
                    <KanbanCard key={blog._id} onClick={() => blog.content ? openPreview(blog) : openEditBlog(blog)}>
                      <CardTitle>{blog.title}</CardTitle>
                      {blog.description && <CardDesc>{blog.description}</CardDesc>}
                      {blog.publishedUrl && (
                        <div style={{ fontSize: 10, color: "#6366f1", marginBottom: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          onClick={(e) => { e.stopPropagation(); window.open(blog.publishedUrl, "_blank"); }}>
                          {blog.publishedUrl}
                        </div>
                      )}
                      <CardFooter>
                        <Tag color={STATUS_CONFIG[blog.status]?.color} style={{ fontSize: 10, margin: 0 }}>
                          {STATUS_CONFIG[blog.status]?.label}
                        </Tag>
                        {blog.wordCount > 0 && (
                          <Tag style={{ fontSize: 10, margin: 0 }}>{blog.wordCount} words</Tag>
                        )}
                        {(blog.keywords || []).slice(0, 2).map((k) => (
                          <Tag key={k} style={{ fontSize: 9, margin: 0 }}>{k}</Tag>
                        ))}
                      </CardFooter>
                      {/* Action buttons */}
                      <div style={{ display: "flex", gap: 4, marginTop: 10, flexWrap: "wrap", overflow: "hidden" }}>
                        {blog.status === "idea" && (
                          <Button
                            size="small" type="primary"
                            icon={generatingBlogId === blog._id ? <LoadingOutlined /> : <ThunderboltOutlined />}
                            loading={generatingBlogId === blog._id}
                            style={{ fontSize: 11, height: 26 }}
                            onClick={(e) => { e.stopPropagation(); handleGenerate(blog._id); }}
                          >
                            Generate
                          </Button>
                        )}
                        {blog.status === "review" && (
                          <>
                            <Button size="small" type="text" icon={<EyeOutlined />} style={{ fontSize: 11, height: 26 }}
                              onClick={(e) => { e.stopPropagation(); openPreview(blog); }}>
                              Preview
                            </Button>
                            <Button size="small" type="text" icon={<CheckCircleOutlined />} style={{ fontSize: 11, height: 26, color: "#10b981" }}
                              onClick={(e) => { e.stopPropagation(); handleStatusChange(blog._id, "published"); }}>
                              Publish
                            </Button>
                          </>
                        )}
                        {blog.status === "published" && (
                          <Button size="small" type="text" icon={<EyeOutlined />} style={{ fontSize: 11, height: 26 }}
                            onClick={(e) => { e.stopPropagation(); openPreview(blog); }}>
                            View
                          </Button>
                        )}
                        {blog.content && blog.status !== "idea" && (
                          <Button size="small" type="text" icon={<ThunderboltOutlined />} style={{ fontSize: 11, height: 26 }}
                            loading={generatingBlogId === blog._id}
                            onClick={(e) => { e.stopPropagation(); handleGenerate(blog._id); }}>
                            Regenerate
                          </Button>
                        )}
                        <Tooltip title="Edit card">
                          <Button size="small" type="text" icon={<EditOutlined />} style={{ fontSize: 11, height: 26 }}
                            onClick={(e) => { e.stopPropagation(); openEditBlog(blog); }} />
                        </Tooltip>
                        <Popconfirm title="Delete this blog?" onConfirm={() => handleDeleteBlog(blog._id)}
                          onClick={(e) => e.stopPropagation()}>
                          <Button size="small" type="text" danger icon={<DeleteOutlined />} style={{ fontSize: 11, height: 26 }}
                            onClick={(e) => e.stopPropagation()} />
                        </Popconfirm>
                      </div>
                    </KanbanCard>
                  ))}
                  <AddCardBtn onClick={() => openCreateBlog(pillar._id)}>
                    <PlusOutlined /> Add Blog
                  </AddCardBtn>
                </KanbanCards>
              </KanbanColumn>
            ))}
            <AddColumnBtn onClick={openCreatePillar}>
              <PlusOutlined style={{ fontSize: 20 }} />
              Add Pillar
            </AddColumnBtn>
          </KanbanContainer>
        </Spin>
      )}

      {/* ── Knowledge Base Tab ─────────────────────────────────────────────── */}
      {activeTab === "knowledge" && (
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setTextDrawerOpen(true); textForm.resetFields(); }}>
              Add Text Knowledge
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              accept=".txt,.md,.csv,.pdf,.doc,.docx"
              style={{ display: "none" }}
              onChange={handleFileSelected}
            />
            <Button icon={<CloudUploadOutlined />} loading={uploading} onClick={() => fileInputRef.current?.click()}>
              Upload Document
            </Button>
            <Button icon={<ThunderboltOutlined />} onClick={() => { setTestModalOpen(true); setTestResults(null); setTestQuery(""); }}>
              Test Knowledge
            </Button>
          </div>

          {knowledgeDocs.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No knowledge documents yet. Add information about Growezy to improve blog generation."
            />
          ) : (
            <KnowledgeGrid>
              {knowledgeDocs.map((doc) => (
                <KnowledgeCard key={doc._id}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10, display: "flex",
                    alignItems: "center", justifyContent: "center",
                    background: doc.isIndexed
                      ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                      : "linear-gradient(135deg, #94a3b8 0%, #64748b 100%)",
                    flexShrink: 0,
                  }}>
                    <FileOutlined style={{ color: "white", fontSize: 18 }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{doc.fileName}</div>
                    <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                      {doc.isIndexed ? (
                        <Tag color="green" style={{ fontSize: 10, margin: 0 }}>Indexed ({doc.chunkCount} chunks)</Tag>
                      ) : (
                        <Tag color="default" style={{ fontSize: 10, margin: 0 }}>Not indexed</Tag>
                      )}
                      {doc.fileType && <span style={{ marginLeft: 8 }}>{doc.fileType}</span>}
                    </div>
                    {doc.description && (
                      <div style={{ fontSize: 11, color: "#64748b", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {doc.description}
                      </div>
                    )}
                  </div>
                  <Popconfirm title="Delete this document?" onConfirm={() => handleDeleteKnowledge(doc._id)}>
                    <Button size="small" danger icon={<DeleteOutlined />} type="text" />
                  </Popconfirm>
                </KnowledgeCard>
              ))}
            </KnowledgeGrid>
          )}
        </div>
      )}

      {/* ── Pillar Drawer ──────────────────────────────────────────────────── */}
      <Drawer
        title={editingPillar ? "Edit Pillar" : "Create Pillar"}
        placement="right" width={440} open={pillarDrawerOpen}
        onClose={() => { setPillarDrawerOpen(false); pillarForm.resetFields(); }}
        destroyOnClose
        footer={
          <div style={{ padding: "12px 0" }}>
            <Button type="primary" loading={pillarSubmitting} block onClick={() => pillarForm.submit()}>
              {editingPillar ? "Save Changes" : "Create Pillar"}
            </Button>
          </div>
        }
      >
        <Form form={pillarForm} layout="vertical" onFinish={handlePillarSubmit}>
          <Form.Item name="title" label="Pillar Title" rules={[{ required: true, message: "Title is required" }]}>
            <Input placeholder="e.g. Creator Monetization" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="What topics will blogs under this pillar cover?" />
          </Form.Item>
          <Form.Item name="keywords" label="Target Keywords (comma separated)">
            <Input placeholder="e.g. creator economy, monetization, content creator tools" />
          </Form.Item>
          <Form.Item name="color" label="Pillar Color">
            <Select
              options={PILLAR_COLORS.map((c) => ({
                value: c,
                label: (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 16, height: 16, borderRadius: 4, background: c }} />
                    {c}
                  </div>
                ),
              }))}
            />
          </Form.Item>
        </Form>
      </Drawer>

      {/* ── Blog Drawer ────────────────────────────────────────────────────── */}
      <Drawer
        title={editingBlog ? "Edit Blog Card" : "Add Blog Card"}
        placement="right" width={440} open={blogDrawerOpen}
        onClose={() => { setBlogDrawerOpen(false); blogForm.resetFields(); }}
        destroyOnClose
        footer={
          <div style={{ padding: "12px 0" }}>
            <Button type="primary" loading={blogSubmitting} block onClick={() => blogForm.submit()}>
              {editingBlog ? "Save Changes" : "Create Blog Card"}
            </Button>
          </div>
        }
      >
        <Form form={blogForm} layout="vertical" onFinish={handleBlogSubmit}>
          <Form.Item name="title" label="Blog Title" rules={[{ required: true, message: "Title is required" }]}>
            <Input placeholder="e.g. How Creators Can Monetize Their Instagram Audience" />
          </Form.Item>
          <Form.Item name="description" label="Description / Brief">
            <Input.TextArea rows={3} placeholder="What should this blog cover? Any specific angle or points to include?" />
          </Form.Item>
          <Form.Item name="keywords" label="Target Keywords (comma separated)">
            <Input placeholder="e.g. instagram monetization, creator tools, audience growth" />
          </Form.Item>
          {editingBlog && (
            <>
              <Form.Item name="status" label="Status" initialValue={editingBlog?.status}>
                <Select
                  options={Object.entries(STATUS_CONFIG).map(([key, cfg]) => ({
                    value: key,
                    label: cfg.label,
                  }))}
                />
              </Form.Item>
              <Form.Item name="publishedUrl" label="Published URL" initialValue={editingBlog?.publishedUrl}>
                <Input placeholder="https://growezy.club/blog/your-blog-slug" />
              </Form.Item>
            </>
          )}
        </Form>
      </Drawer>

      {/* ── Blog Preview/Edit Modal ──────────────────────────────────────── */}
      <Modal
        title={
          <div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{previewBlog?.title}</div>
            {previewBlog?.metaDescription && (
              <div style={{ fontSize: 12, color: "#64748b", fontWeight: 400, marginTop: 4 }}>
                Meta: {previewBlog.metaDescription}
              </div>
            )}
          </div>
        }
        open={previewOpen}
        onCancel={() => { setPreviewOpen(false); setEditMode("preview"); }}
        width={900}
        styles={{ body: { padding: "16px 24px" } }}
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", gap: 8 }}>
              {editMode !== "preview" && (
                <>
                  <Button onClick={() => { setEditMode("preview"); }}>Cancel</Button>
                  <Button type="primary" loading={contentSaving} onClick={handleSaveContent}>Save</Button>
                </>
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {editMode === "preview" && (
                <Button icon={<EditOutlined />} onClick={() => { setContentValue(previewBlog?.content || ""); setEditMode("visual"); }}>
                  Edit
                </Button>
              )}
              {previewBlog?.status === "review" && (
                <Button type="primary" icon={<CheckCircleOutlined />}
                  onClick={() => { handleStatusChange(previewBlog._id, "published"); setPreviewOpen(false); }}>
                  Publish
                </Button>
              )}
            </div>
          </div>
        }
      >
        {previewBlog && (
          <>
            {/* AI Enhancement bar */}
            <div style={{
              display: "flex", gap: 8, marginBottom: 16, padding: 12,
              background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0",
            }}>
              <Input
                placeholder="Tell AI how to improve this blog... e.g. 'add more examples', 'make intro more engaging', 'add a section about pricing'"
                value={enhancePrompt}
                onChange={(e) => setEnhancePrompt(e.target.value)}
                onPressEnter={handleEnhance}
                style={{ flex: 1 }}
              />
              <Button
                type="primary" icon={<ThunderboltOutlined />}
                loading={enhancing} onClick={handleEnhance}
                disabled={!enhancePrompt.trim()}
              >
                Enhance
              </Button>
            </div>

            {/* Content area */}
            {editMode === "preview" && (
              <BlogPreview dangerouslySetInnerHTML={{ __html: previewBlog.content || "<p>No content generated yet.</p>" }} />
            )}
            {editMode === "visual" && (
              <div style={{ background: "white", borderRadius: 8, maxHeight: "55vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <ReactQuill
                  theme="snow"
                  value={contentValue}
                  onChange={setContentValue}
                  style={{ flex: 1, overflow: "auto", maxHeight: "50vh" }}
                  modules={{
                    toolbar: [
                      [{ header: [2, 3, 4, false] }],
                      ["bold", "italic", "underline", "strike"],
                      [{ list: "ordered" }, { list: "bullet" }],
                      ["blockquote", "link"],
                      ["clean"],
                    ],
                  }}
                />
              </div>
            )}
          </>
        )}
      </Modal>

      {/* ── Text Knowledge Drawer ──────────────────────────────────────────── */}
      <Drawer
        title="Add Text Knowledge"
        placement="right" width={500} open={textDrawerOpen}
        onClose={() => { setTextDrawerOpen(false); textForm.resetFields(); }}
        destroyOnClose
        footer={
          <div style={{ padding: "12px 0" }}>
            <Button type="primary" loading={textSubmitting} block onClick={() => textForm.submit()}>
              Add & Index
            </Button>
          </div>
        }
      >
        <Form form={textForm} layout="vertical" onFinish={handleAddTextKnowledge}>
          <Form.Item name="title" label="Title" rules={[{ required: true, message: "Title is required" }]}>
            <Input placeholder="e.g. Growezy Features Overview" />
          </Form.Item>
          <Form.Item name="text" label="Content" rules={[{ required: true, message: "Content is required" }]}>
            <Input.TextArea rows={15} placeholder="Paste information about Growezy, its features, use cases, etc." />
          </Form.Item>
        </Form>
      </Drawer>

      {/* ── Test Knowledge Modal ───────────────────────────────────────────── */}
      <Modal
        title="Test Knowledge Base"
        open={testModalOpen}
        onCancel={() => setTestModalOpen(false)}
        width={700}
        footer={null}
      >
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <Input
            placeholder="Ask a question, e.g. 'What features does Growezy offer for creators?'"
            value={testQuery}
            onChange={(e) => setTestQuery(e.target.value)}
            onPressEnter={handleTestKnowledge}
            style={{ flex: 1 }}
          />
          <Button type="primary" loading={testLoading} onClick={handleTestKnowledge}>
            Search
          </Button>
        </div>
        {testResults && (
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b", marginBottom: 12 }}>
              {testResults.resultsCount} chunk{testResults.resultsCount !== 1 ? "s" : ""} found
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: "50vh", overflowY: "auto" }}>
              {testResults.chunks.map((chunk, i) => (
                <div key={i} style={{
                  padding: 12, borderRadius: 8, background: "#f8fafc",
                  border: "1px solid #e2e8f0", fontSize: 12, color: "#334155",
                  lineHeight: 1.6, whiteSpace: "pre-wrap",
                }}>
                  <Tag style={{ marginBottom: 6, fontSize: 10 }}>Chunk {i + 1}</Tag>
                  <div>{chunk}</div>
                </div>
              ))}
              {testResults.resultsCount === 0 && (
                <div style={{ color: "#94a3b8", fontSize: 13, textAlign: "center", padding: 20 }}>
                  No relevant chunks found. Try uploading more documents or rephrasing your query.
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </PageContainer>
  );
}

export default SEOBlogsPage;
